import { useCallback, useEffect, useMemo, useRef, type ReactElement } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { AutoAwesome } from '@mui/icons-material'
import { Box, Button, useTheme } from '@mui/material'
import { purple } from '@mui/material/colors'
import { Html } from '@react-three/drei'
import { type SnackbarKey, useSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'
import { useControlStore, type SmartSuggestionStoreItem } from '@editorStores'
import { useSceneElement, useSceneMarker } from '@editorHooks'

const inactiveScenePoint = { x: 0, y: 0, z: 0 }

type ScenePoint = { x: number; y: number; z: number }

const SmartSuggestionSceneIcon = ({ point }: { point: ScenePoint }): ReactElement => (
  <Html position={[point.x, point.y, point.z]} center zIndexRange={[1, 1]}>
    <Box
      component="span"
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'common.white',
        bgcolor: purple.A200,
        boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
        fontSize: 16,
        lineHeight: 1,
        pointerEvents: 'none',
        fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 20",
        transform: 'translate(100%, -100%)',
      }}
    >
      <AutoAwesome />
    </Box>
  </Html>
)

const SmartSuggestionsController = (): ReactElement | null => {
  const theme = useTheme()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const snackbarKeyRef = useRef<SnackbarKey | null>(null)
  const activeSuggestion = useControlStore(
    state => state.smartSuggestions.find(suggestion => suggestion.enabled) ?? null,
  )
  const smartSuggestionsEnabled = useAppStore(state => state.smartSuggestionsEnabled)
  const setSmartSuggestionEnabled = useControlStore(state => state.setSmartSuggestionEnabled)
  const activeScenePoint =
    activeSuggestion?.type === 'scene-point-suggestion' ? activeSuggestion.point : null
  const sceneIconProperties = useMemo(
    () => ({ point: activeScenePoint ?? inactiveScenePoint }),
    [activeScenePoint],
  )

  useSceneMarker({
    marker: activeScenePoint ?? inactiveScenePoint,
    enabled: !!activeScenePoint,
    color: purple.A200,
    disableDepthTest: true,
  })
  useSceneElement({
    enabled: !!activeScenePoint,
    component: SmartSuggestionSceneIcon,
    properties: sceneIconProperties,
  })

  const closeActiveSnackbar = useCallback(() => {
    if (snackbarKeyRef.current == null) return

    closeSnackbar(snackbarKeyRef.current)
    snackbarKeyRef.current = null
  }, [closeSnackbar])

  const dismissSuggestion = useCallback(
    (suggestion: SmartSuggestionStoreItem) => {
      try {
        suggestion.onAbort?.()
      } finally {
        setSmartSuggestionEnabled(suggestion.id, false)
      }
      closeActiveSnackbar()
    },
    [closeActiveSnackbar, setSmartSuggestionEnabled],
  )

  const acceptSuggestion = useCallback(
    (suggestion: SmartSuggestionStoreItem) => {
      try {
        suggestion.callback()
      } finally {
        setSmartSuggestionEnabled(suggestion.id, false)
        closeActiveSnackbar()
      }
    },
    [closeActiveSnackbar, setSmartSuggestionEnabled],
  )

  useEffect(() => {
    closeActiveSnackbar()

    if (!activeSuggestion || !smartSuggestionsEnabled) return undefined

    const snackbarKey = enqueueSnackbar(activeSuggestion.text, {
      persist: true,
      preventDuplicate: true,
      style: {
        backgroundColor: purple.A200,
        color: theme.palette.common.white,
      },
      action: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button size="small" color="inherit" onClick={() => acceptSuggestion(activeSuggestion)}>
            Akzeptieren (a)
          </Button>
          <Button size="small" color="inherit" onClick={() => dismissSuggestion(activeSuggestion)}>
            Verwerfen (Esc)
          </Button>
        </Box>
      ),
    })

    snackbarKeyRef.current = snackbarKey

    return () => {
      closeSnackbar(snackbarKey)
      if (snackbarKeyRef.current === snackbarKey) snackbarKeyRef.current = null
    }
  }, [
    acceptSuggestion,
    activeSuggestion,
    closeActiveSnackbar,
    closeSnackbar,
    dismissSuggestion,
    enqueueSnackbar,
    theme.palette.common.white,
    smartSuggestionsEnabled,
  ])

  // react-hotkeys-hook ignores key events originating from form fields by
  // default, so "a" stays typeable while editing text and only triggers the
  // accept shortcut otherwise.
  const hotkeysEnabled = !!activeSuggestion && smartSuggestionsEnabled
  useHotkeys(
    'a',
    () => activeSuggestion && acceptSuggestion(activeSuggestion),
    { enabled: hotkeysEnabled, preventDefault: true },
    [activeSuggestion, acceptSuggestion],
  )
  useHotkeys(
    'esc',
    () => activeSuggestion && dismissSuggestion(activeSuggestion),
    { enabled: hotkeysEnabled, preventDefault: true },
    [activeSuggestion, dismissSuggestion],
  )

  return null
}

export default SmartSuggestionsController
